import { ISelectItem, MultiPanelTile, Pill, SingleSelect, useSettings } from "@react-gcc-eds/core";
import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { IDashboardItemSelected } from "..";
import { useMetricsActions } from "../../../../actions/metrics-actions";
import { useMetricsFiltersActions } from "../../../../actions/metrics-filters-actions";
import { useSettingsActions } from "../../../../actions/settings-actions";
import { DashboardTooltipHeader } from "../../../../components/shared/tooltip-header";
import { deliveryRecurrences, IDeliveryRecurrence, useStore } from "../../../../store";
import { dateAsYMDString } from "../../../../utils";
import { useLocalization } from "../../../../utils/localization";
import DatePicker from "../../../filters/date-picker";
import { usePredefinedRanges } from "../../../filters/date-picker/predefined-ranges";
import CustomTabs, { ITab } from "../../../shared/custom-tabs";
import { ShipmentDeliveredBarChart } from "../bars-chart";
import { DashboardTileAlignment as TileAlignment } from "../index";
import { NoMetrics } from "../utils";

const ShipmentDelivered = ({
  labels,
  className,
  customerId,
  alignment
}: {
  labels: { [key: string]: string | undefined };
  className: string;
  customerId: string;
  alignment: TileAlignment;
  onItemSelected?(data: IDashboardItemSelected): void;
}) => {
  const tileRef = useRef<HTMLDivElement>(null);
  const { currentCustomer } = useSettingsActions();
  const [showFilter, setShowFilter] = useState(false);
  const [selectedTab, setSelectedTab] = useState<ITab>();
  const [tabs, setTabs] = useState<ITab[]>([]);
  const [state] = useStore();
  const [recurrence, setRecurrence] = useState<IDeliveryRecurrence>(deliveryRecurrences[1]);
  const { shipmentDeliveredMetrics: shipmentDeliveredMetricLoading } = state.fetchingData.metrics;
  const { localeString } = useLocalization();

  const maxWidth = useMemo(() => {
    if (tileRef.current) {
      return tileRef.current.clientWidth;
    }
  }, [tileRef.current, window.innerWidth]);

  const { rangeAsString, largestRange, isLargestRange } = usePredefinedRanges(
    new Date(currentCustomer().dataAvailableFromDate)
  );
  const { getShipmentDeliveredMetrics } = useMetricsActions();
  const { setMetricsShipmentStatusBarsDeliveredRange } = useMetricsFiltersActions();

  const { theme } = useSettings();

  useEffect(() => {
    if (state.metricsFilters.shipmentDeliveredRange) {
      const {
        shipmentDeliveredRange: { from, to }
      } = state.metricsFilters;
      getShipmentDeliveredMetrics(recurrence.value, dateAsYMDString(from), dateAsYMDString(to));
    }
  }, [state.metricsFilters.shipmentDeliveredRange, customerId, recurrence]);

  useEffect(() => {
    const tabs = [];
    if (labels["purchaseOrders"] !== undefined) {
      tabs.push(shipmentValueTab);
    }
    if (tabs.length) {
      setTabs(tabs);
      if (!selectedTab) {
        setSelectedTab(tabs[0]);
      }
    }
  }, [shipmentDeliveredMetricLoading]);

  const shipmentValue = () => {
    const { shipmentDeliveredMetrics } = state.metrics;
    return currentCustomer().isUsingFinancialData ? (
      <ShipmentDeliveredBarChart
        metrics={shipmentDeliveredMetrics}
        loading={shipmentDeliveredMetricLoading === true}
        unit={shipmentDeliveredMetrics.currency}
      />
    ) : shipmentDeliveredMetricLoading ? (
      <NoMetrics loading />
    ) : (
      <NoMetrics />
    );
  };

  const resetFilter = () => {
    setMetricsShipmentStatusBarsDeliveredRange(largestRange.selector());
  };

  const handleRecurrenceChanged = (recurrence: IDeliveryRecurrence) => {
    setRecurrence(recurrence);
    // setFilterChanged(true);
  };

  const resetFilterPill = () => {
    return (
      <Pill className={cx("filter-pill", theme)} onToggle={resetFilter}>
        Reset filters
      </Pill>
    );
  };

  const filtersSummary = (
    <div className="dashboard-active-filter">
      <Pill>{rangeAsString(state.metricsFilters.shipmentDeliveredRange)}</Pill>
      {!isLargestRange(state.metricsFilters.shipmentDeliveredRange) ? resetFilterPill() : null}
    </div>
  );

  const shipmentValueTab = {
    view: shipmentValue(),
    title: "Shipment value",
    index: 0
  } as ITab;

  const dateFilter = (
    <DatePicker
      dual={state.userPreferences.useDualCalendar}
      rolling
      selectedRange={state.metricsFilters.shipmentDeliveredRange}
      oldestDate={new Date(currentCustomer().dataAvailableFromDate)}
      onRangeSelected={setMetricsShipmentStatusBarsDeliveredRange}
      key="date-picker"
      className="filter"
    />
  );

  const periodFilter = (
    <SingleSelect
      className="medium"
      items={deliveryRecurrences.map(r => ({ title: r.description, value: r.value.toString() }))}
      label={recurrence?.description}
      onChange={(v: ISelectItem) =>
        handleRecurrenceChanged(
          deliveryRecurrences.find(r => r.value.toString() === v.value) ?? deliveryRecurrences[0]
        )
      }
    />
  );

  const tileHeader = (
    <div className="dashboard-tile">
      <DashboardTooltipHeader
        tooltipInfo="Graph is showing the status for the selected time period"
        alignment={alignment}
        header={localeString("Dashboard_ShipmentDelivered") || "Shipment delivered"}
      />
    </div>
  );

  return (
    <>
      <MultiPanelTile
        centerTitle={"Shipment delivered"}
        centerContent={<CustomTabs onTabChange={(tab: ITab) => setSelectedTab(tab)} tabs={tabs} />}
        centerRightActions={periodFilter}
        className={className}
        lg={alignment === TileAlignment.FULL ? 12 : 6}
        sm={12}
        leftContent={dateFilter}
        leftTitle={tileHeader}
        leftVisible={showFilter}
        leftSubtitle={filtersSummary}
      />
    </>
  );
};

export default ShipmentDelivered;
